import {
  CbhIcon,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Title,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Stack, ThemeProvider } from "@mui/material";
import { getFormattedAddressLines } from "@src/appV2/Address/utils/getFormattedAddressLines";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { formatUsPhoneNumber } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { ACCOUNTS_BASE_PATH } from "@src/appV2/redesign/Accounts/paths";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { INDEPENDENT_CONTRACTOR_BADGE_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { AnalyticsOpenFromValues } from "@src/appV2/redesign/IndependantContractorBadge/constants";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { USER_EVENTS } from "@src/constants";
import { useRef } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../components/Button";
import { ProfileImage } from "../WorkWithFriends/components/ProfileImage";
import {
  UPDATE_EMAIL_FULL_PATH,
  UPDATE_PHONE_FULL_PATH,
  UPDATE_PROFILE_IMAGE_FULL_PATH,
} from "./paths";

export function ProfilePage() {
  const worker = useDefinedWorker();
  const history = useHistory();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: workerProfileImageUrl } = useWorkerProfilePicture(worker.userId);

  useLogEffect(USER_EVENTS.VIEWED_PROFILE);

  const formattedAddress = getFormattedAddressLines(worker.address).join(", ");

  const updateEmail = () => {
    logEvent(USER_EVENTS.PROFILE_CHANGE_STARTED, {
      type: "EMAIL",
    });
    history.push(UPDATE_EMAIL_FULL_PATH);
  };

  const updatePhone = () => {
    logEvent(USER_EVENTS.PROFILE_CHANGE_STARTED, {
      type: "PHONE",
    });
    history.push(UPDATE_PHONE_FULL_PATH);
  };

  const updateProfileImage = () => {
    history.push(UPDATE_PROFILE_IMAGE_FULL_PATH);
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Profile"
          scrollContainerRef={scrollRef}
          fallbackGoBackPath={ACCOUNTS_BASE_PATH}
          headerVariant="tertiary"
        />

        <PageContent ref={scrollRef}>
          <Stack spacing={2}>
            <Stack
              spacing={6}
              alignItems="center"
              sx={{
                paddingY: 6,
              }}
            >
              <Title semibold variant="h4" component="h2" sx={{ textAlign: "center" }}>
                {worker.name}
              </Title>
              <ProfileImage
                size="large"
                firstName={worker.firstName ?? ""}
                lastName={worker.lastName ?? ""}
                src={workerProfileImageUrl?.distribution_url}
              />
              <Button startIconType="pencil" variant="contained" onClick={updateProfileImage}>
                Upload Photo
              </Button>
            </Stack>

            <List
              component="div"
              sx={{
                "& li": {
                  height: 62,
                },
              }}
            >
              <ListItem divider>
                <ListItemButton hideCallToActionIcon onClick={updateEmail}>
                  <ListItemIcon iconType="email-filled" />
                  <ListItemText primary="Email" secondary={worker.email} />
                  <CbhIcon
                    type="pencil"
                    sx={{
                      width: 18,
                      height: 18,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem divider>
                <ListItemButton hideCallToActionIcon onClick={updatePhone}>
                  <ListItemIcon iconType="phone" />
                  <ListItemText primary="Phone" secondary={formatUsPhoneNumber(worker.phone)} />
                  <CbhIcon
                    type="pencil"
                    sx={{
                      width: 18,
                      height: 18,
                    }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem divider>
                <ListItemButton
                  onClick={() => {
                    history.push(DeprecatedGlobalAppV1Paths.ADDRESS);
                  }}
                >
                  <ListItemIcon iconType="map-pin" />
                  <ListItemText primary="Home Address" secondary={formattedAddress} />
                </ListItemButton>
              </ListItem>

              <ListItem divider>
                <ListItemButton
                  onClick={() => {
                    logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_OPENED, {
                      workerId: worker.userId,
                      workerFullName: `${worker.firstName} ${worker.lastName}`,
                      openedFrom: AnalyticsOpenFromValues.PROFILE,
                    });
                    history.push(INDEPENDENT_CONTRACTOR_BADGE_FULL_PATH);
                  }}
                >
                  <ListItemIcon iconType="clipboard" />
                  <ListItemText
                    primary="Work Badge"
                    secondary="Create your badge to wear during your shifts"
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Stack>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
